import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, ViewRef, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { Title } from '@angular/platform-browser';
import {
  AppStore,
  BroadcastService,
  ChangedDocumentService,
  ClientConstants,
  ComponentService,
  ConfigService,
  CurrentDocumentInfoService,
  CurrentDocumentService,
  DataService,
  DocumentSaveService,
  DocumentService,
  DocumentSessionService,
  FormColorService,
  IApplicationState,
  LocalStorageKeys,
  LocalStorageService,
  LoggedInUserService,
  ModifiedCSSService,
  ModifiedFieldService,
  NavigationService,
  NotificationService,
  OfflineStatusService,
  ValidationService,
  formDeleteDocument,
  formSetDocument,
  select
} from '@formbird/services';
import { UtilType } from '@formbird/shared';
import { ComponentDocument, DocumentData, RulesetDocumentContext, SharedConstants, User } from '@formbird/types';
import { ImageLinkService } from '@services/image-link.service';
import { ModalService } from '@services/modal/modal.service';
import * as async from 'async';
import { cloneDeep } from 'es-toolkit';
import * as lodashArray from 'lodash/array';
import * as lodashString from 'lodash/string';
import { Observable, Subscription } from 'rxjs';
import { RouterService } from '../../../app-routing/router.service';
import { DynamicContainerComponent } from './dynamic-container/dynamic-container.component';
import { MatIconModule } from '@angular/material/icon';

const logger = console;

@Component({
  selector: 'ft-template',
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MatCardModule, MatIconModule, DynamicContainerComponent, NgStyle, NgClass]
})
export class TemplateComponent implements OnInit, OnChanges, OnDestroy {
  private formColorService = inject(FormColorService);
  private imageLinkService = inject(ImageLinkService);
  private currentDocumentInfoService = inject(CurrentDocumentInfoService);
  private currentDocumentService = inject(CurrentDocumentService);
  private configService = inject(ConfigService);
  private modifiedCSSService = inject(ModifiedCSSService);
  private navigationService = inject(NavigationService);
  private componentService = inject(ComponentService);
  private documentService = inject(DocumentService);
  private validationService = inject(ValidationService);
  private titleService = inject(Title);
  private broadcastService = inject(BroadcastService);
  private loggedInUserService = inject(LoggedInUserService);
  private appStore = inject<AppStore<IApplicationState>>(AppStore);
  private cdr = inject(ChangeDetectorRef);
  private changedDocumentService = inject(ChangedDocumentService);
  private documentSessionService = inject(DocumentSessionService);
  private el = inject(ElementRef);
  private notificationService = inject(NotificationService);
  private modifiedFieldService = inject(ModifiedFieldService);
  private localStorageService = inject(LocalStorageService);
  private routerService = inject(RouterService);
  private documentSaveService = inject(DocumentSaveService);
  private modalService = inject(ModalService);
  private dataService = inject(DataService);
  private offlineStatusService = inject(OfflineStatusService);
  private defaultAnimationLoadImage = 'images/loading-spinner.svg';

  @Input() formParameters;
  @Input() user: User;
  @ViewChild('ngCont', { read: ViewContainerRef }) viewContainerRef;
  @ViewChild('ngTpl', { read: TemplateRef }) ngTpl: TemplateRef<any>;

  componentIndex: number;
  documentData: DocumentData;
  responsiveLayouts: any;
  componentsRegistered = false;
  errorMsg: string;
  isDeleted: boolean = false;
  private subs: Subscription = new Subscription();
  components: any;
  isOffline = false;
  showNetworkMsg = false;
  state = 'shown';
  functionComponentData = {};
  animationBGImageUrl: any;
  @select(['formState', 'documents']) documents$: Observable<any>;
  @select(['formState', 'templates']) templates$: Observable<any>;
  @select(['formState', 'documentInfo']) documentInfo$: Observable<any>;
  @select(['formState', 'recordedComponentTemplateChanges']) recordedComponentTemplateChanges$: Observable<any>;

  constructor() {
    this.componentIndex = 0;
  }

  async loadData() {
    this.componentsRegistered = false;
    this.errorMsg = null;

    if (this.loggedInUserService.getUserConfigItem('isRunningCacheClear')){
        await this.clearCache();
    }

    if (this.formParameters && this.formParameters.selectedDocumentId) {

      console.log('Loading html for document/template: ' + this.formParameters.selectedDocumentId);

      const unsavedDocumentListId = this.formParameters.unsavedDocumentListId;
      if (!unsavedDocumentListId) {
        this.errorMsg = 'An unsaved document group id must be passed to load data.';
        return;
      }


      this.documentData = this.currentDocumentInfoService.getDocumentData(unsavedDocumentListId, this.formParameters.selectedDocumentId, this.formParameters.overrideTemplateId);

      if (this.documentData && this.documentData.template && this.documentData.template.enableTemplateLoadAnimation) {
        this.state = !this.documentData.template.enableTemplateLoadAnimation ? 'disable' : 'shown';
      } else {
        this.state =  !this.configService.clientConfig().enableTemplateLoadAnimation ? 'disable' : 'shown';
      }

      if (this.documentData && this.formParameters.defaultFields) {
        this.documentData.document = cloneDeep(this.formParameters.defaultFields);
      }

      // formParameters.overrideTemplateId will only be set if there is an override template id set in the URL
      this.documentService.getTemplateForDocument(this.formParameters, this.documentData).then(
          async (result) => {

          if (this.formParameters.isMainDoc) {

            this.currentDocumentService.setMainDocumentListId(unsavedDocumentListId, this.documentData.documentId);
          }

          this.isOffline = false;

          const isEnableAnimation = this.documentData.template.enableTemplateLoadAnimation;
          if (this.documentData.template && typeof(isEnableAnimation) !== 'undefined' && isEnableAnimation !== null ) {
            this.state = !isEnableAnimation ? 'disable' : 'shown';
          } else {
            this.state =  !this.configService.clientConfig().enableTemplateLoadAnimation ? 'disable' : 'shown';
          }
          
          // Only load the animation image if the animation is enabled
          if (this.state === 'shown') {
            this.animationBGImageUrl = this.documentData.template.formLoadingImageURL || this.defaultAnimationLoadImage;
          }

          if (this.documentData.template && this.documentData.template.documentId) {

            this.addDisableSaveFieldValues();

            this.setDocumentTitle();

            this.setFormLayout();

            await this.componentService.loadTemplateVendorLibs(this.documentData.template);
            
            this.setWatchComponentTemplate();
            
            this.documentSessionService.initialize(this.documentData.document.documentId);
            
            await this.doPreRender();

            if (this.documentData.template.loadComponentDocumentDependencies === false){
              this.appendComponents();

            } else{
              await this.registerAndAppendComponents();
            }

            this.componentsRegistered = true;

            this.updateTemplateSettings();

            this.doOnLoad();

            this.restoreWIPDocument(result, unsavedDocumentListId);
          } else {
            const msg = 'Template not found: ' + this.formParameters.selectedDocumentId;
            this.updateError(msg);
          }
        },
        err => {
          if (err.status === 0) {
            this.isOffline = true;
          }

          this.handleLoadError(err);
        });

    } else {
      this.currentDocumentService.resetDocumentData(this.formParameters?.unsavedDocumentListId);
      this.updateError('No selected document to load.');
    }
  }

  ngOnInit() {
    if (!this.user) {
      this.user = this.loggedInUserService.getLoggedInUser();
    }

    this.loadData();

    const formChangeSub = this.broadcastService.on(ClientConstants.FORM_DETECT_CHANGES).subscribe(data => {
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });

    const documentSub = this.documents$.subscribe(documents => {
      if (this.documentData) {
        const documentId = this.documentData.documentId;
        const document = documents[documentId];

        if (document) {
          this.documentData.document = cloneDeep(document);
        }
      }
    });

    const templateSub = this.templates$.subscribe(templates => {
      if (this.documentData) {
        const templateId = this.documentData.templateId;
        const template = templates[templateId];

        if (template) {
          this.documentData.template = cloneDeep(template);
        }
      }
    });

    this.subs.add(formChangeSub);
    this.subs.add(documentSub);
    this.subs.add(templateSub);

    (<any>window).addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.broadcastService.broadcast(ClientConstants.BEFORE_INSTALL_PROMPT, e);
    });


  }

  ngOnChanges(data) {
    if (this.viewContainerRef) {
      this.viewContainerRef.clear();
    }

    // only reload component if there have changes from formParameters
    if (data?.formParameters?.previousValue) {
      this.loadData();
    }
  }

  private handleLoadError(err) {
    this.updateError(err);

    this.currentDocumentService.resetDocumentData(this.formParameters?.unsavedDocumentListId);
    this.broadcastService.broadcast(ClientConstants.DOCUMENT_LOAD_ERROR, true);
  }

  private updateError(err) {
    this.errorMsg = err?.message ? err.message : err;

    if (this.errorMsg?.includes('Unknown Error') || this.errorMsg?.includes('Network Error')) {
      this.errorMsg = '';
      this.showNetworkMsg = true;
    }

    logger.log(this.errorMsg);

    this.setFormLayout();
  }

  private setFormLayout() {
    if (this.formParameters.isMainDoc) {
      const tplData = this.documentData.template;
      const docData = this.documentData.document;

      this.formColorService.setColor(tplData?.formColor ? tplData?.formColor : '');
      this.formColorService.setColorDark(tplData?.formColorDark ? tplData?.formColorDark : '');

      if (tplData?.commandBarCSS) {
        this.modifiedCSSService.setCommandBarCSS(tplData?.documentId, tplData?.commandBarCSS);
      }

      if (docData && docData.systemHeader) {
        const systemHeader: any = docData.systemHeader;
        this.imageLinkService.setImageFileNo96(systemHeader.imageFileNo96);
      }
    }
  }

  /**
   * set the document title. This will display at the top of the tab or window
   */
  setDocumentTitle() {
    
    if (!this.formParameters.isMainDoc) {
      return;
    }

    const document = this.documentData.document;
    const template = this.documentData.template;
    const title = document.systemHeader.summaryName 
              || template.systemHeader.summaryName 
              || this.configService.clientConfig().browserDocumentTitle 
              || SharedConstants.APP_NAME;

    const prefix = this.localStorageService.getItem(LocalStorageKeys.TITLE_PREFIX) || '';
    this.localStorageService.removeItem(LocalStorageKeys.TITLE_PREFIX);

    this.titleService.setTitle(prefix + title);
  }

  private updateTemplateSettings() {
    if (this.formParameters.isMainDoc) {
      this.navigationService.setCurrentTemplate(this.documentData.template);
    }
  }

  private async doPreRender() {
    const docContext: RulesetDocumentContext = {
      documentData: this.documentData,
      template: this.documentData.template,
      templateId: this.documentData.template.documentId,
      formParameters: this.formParameters,
      isNew: this.documentData?.isNew
    };

    return new Promise((resolve, reject) => {
      this.validationService.preRenderDocument(this.documentData.document, docContext,
        (error, result) => {
          if (error) {
            reject(error);
          } else {
            this.validationService.broadcastValidationEvent();
            resolve(result);
          }
      });
    });
  }

  private doOnLoad() {

    const docContext: RulesetDocumentContext = {
      documentData: this.documentData,
      template: this.documentData.template,
      templateId: this.documentData.template.documentId,
      formParameters: this.formParameters,
      isNew: this.documentData?.isNew
    };

    this.validationService.onLoadDocument(this.documentData.document, docContext,
      (error, result) => {
        const hash = window.location.hash;
        if (hash) {
          this.waitForElementAndScroll(hash);
        }

        if (result) {
          this.validationService.broadcastValidationEvent();
          this.onLoadCb(null);
        } else {
          this.onLoadCb(error);
        }
        this.state = 'hidden';
      });
  }

  private waitForElementAndScroll(hash: string): void {
    const container = window.document.querySelector('#ft-sidenav-content > ft-template-view > div');
    if (!container) {
      return;
    }

    const TIMEOUT_MS = 5000;
    const DOM_STABILITY_DELAY = 200; // Wait 200ms of no DOM activity
    const startTime = Date.now();
    let domStabilityTimer: any = null;
    let timeoutTimer: any = null;
    let mutationObserver: MutationObserver | null = null;
    let pollInterval: any = null;
    let isResolved = false;

    const cleanup = () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
      if (domStabilityTimer) {
        clearTimeout(domStabilityTimer);
        domStabilityTimer = null;
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
      }
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    // Extract the ID from the hash (remove # if present)
    const elementId = hash.startsWith('#') ? hash.substring(1) : hash;

    const scrollToElement = () => {
      try {
        // Use getElementById which works with IDs starting with digits
        const element = window.document.getElementById(elementId);
        if (element) {
          element.scrollIntoView();
        }
      } catch (e) {
        logger.warn('Error scrolling to element:', e);
      }
    };

    const resolve = () => {
      if (isResolved) return;
      isResolved = true;
      cleanup();
      scrollToElement();
    };

    const checkElement = () => {
      if (isResolved) return;

      try {
        // Use getElementById which works with IDs starting with digits
        const element = window.document.getElementById(elementId);
        if (element) {
          // Element found, resolve immediately
          resolve();
          return;
        }
      } catch (e) {
        // getElementById error, continue waiting
      }

      // Check timeout
      if (Date.now() - startTime >= TIMEOUT_MS) {
        resolve();
      }
    };

    // Set up MutationObserver to detect DOM activity
    mutationObserver = new MutationObserver(() => {
      // Reset stability timer on any mutation
      if (domStabilityTimer) {
        clearTimeout(domStabilityTimer);
        domStabilityTimer = null;
      }

      // If no DOM activity detected for DOM_STABILITY_DELAY, resolve
      domStabilityTimer = setTimeout(() => {
        if (!isResolved) {
          resolve();
        }
      }, DOM_STABILITY_DELAY);

      // Also check if element exists
      checkElement();
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Set up timeout
    timeoutTimer = setTimeout(() => {
      resolve();
    }, TIMEOUT_MS);

    // Initial check
    checkElement();

    // Poll for element existence and timeout check
    pollInterval = setInterval(() => {
      if (isResolved) {
        cleanup();
        return;
      }
      checkElement();
    }, 100);
  }

  private onLoadCb(error?) {

    if (error) {
      this.updateTemplateSettings();
      const msg = error.message;
      this.handleLoadError(msg);

    } else {
      this.broadcastService.broadcast(ClientConstants.DOCUMENT_LOADED, {
        documentData: this.documentData,
        formParameters: this.formParameters
      });
      this.changedDocumentService.documentLoaded(this.documentData.documentId, this.formParameters.unsavedDocumentListId, true);
      
      this.onDocumentSaved();
      
    }

  }

  private onDocumentSaved() {
    const savedSub: Subscription = this.documentSaveService.onDocumentSaved().subscribe((documentIds) => {
      console.log('Saved documents: ', documentIds);
      if (documentIds.includes(this.documentData?.documentId)) {
        const systemHeader: any = this.documentData?.document?.systemHeader;
        if (systemHeader?.deleted) {
          this.updateError('Document has been deleted');
          this.isDeleted = true;
          this.changedDocumentService.dispatchAction(formDeleteDocument(this.documentData?.documentId));
          this.modifiedFieldService.resetFormDirty();
          this.cdr.detectChanges();
        } else {
          // refresh the browser tab title in case the save changed summaryName
          this.setDocumentTitle();
        }
      }
    });
    this.subs.add(savedSub);

    this.subs.add(
      this.broadcastService.on(ClientConstants.DOCUMENT_UPDATED).subscribe((versions) => {
        const updated = Array.isArray(versions) ? versions : [versions];
        if (updated.some((version) => version?.documentId === this.documentData?.documentId)) {
          // documentData.document is kept in sync with the store by documentSub above,
          // so summaryName is already up to date here. isMainDoc is guarded inside setDocumentTitle.
          this.setDocumentTitle();
        }
      })
    );

    this.broadcastService.on(ClientConstants.DOCUMENT_DELETED).subscribe((documentId) => {
      if (documentId === this.documentData?.documentId) {
        if (this.documentData?.document?.systemHeader?.systemType === SharedConstants.SYSTEM_TYPE_FILE_REFERENCE) {          
          this.dataService.destroyFileFromCache(this.documentData.document);
        }
        this.updateError('Document has been deleted');
        this.isDeleted = true;
        this.changedDocumentService.dispatchAction(formDeleteDocument(documentId));
        this.modifiedFieldService.resetFormDirty();
        this.cdr.detectChanges();
      }
    });
  }

  setWatchComponentTemplate() {
    const _self = this;
    const compSub = this.changedDocumentService.watchComponentTemplate(this.documentData.template.documentId).subscribe(changes => {

      _self.addDisableSaveFieldValues();

      _self.components = changes;

      if (!(_self.cdr as ViewRef).destroyed) {
        _self.cdr.detectChanges();
      }

    });

    this.subs.add(compSub);

    this.watchComponentStateChange();

  }
  /**
   * function for improving the performance of the ngFor used to render the components in the html file for this component
   */
  trackByFn(index, item) {
    return index.name;
  }

  ngOnDestroy() {
    if (this.documentData?.document) {
      const documentId = this.documentData.document.documentId;
      const unsavedDocumentListId = this.formParameters.unsavedDocumentListId;
      this.broadcastService.broadcast(ClientConstants.DOCUMENT_DESTROYED, {
        documentId,
        unsavedDocumentListId
      });

      const curUrl = this.routerService.getCurrentUrl();
      if (this.documentData?.isNew && !curUrl?.endsWith(documentId) ) {
        setTimeout(() => {
          this.changedDocumentService.documentLoaded(documentId, unsavedDocumentListId, false);
        }, 500);
      } else {
        this.changedDocumentService.documentLoaded(documentId, unsavedDocumentListId, false);
      }

    }

    this.subs.unsubscribe();
  }

  async runComponentFunction(componentDef) {
    const anyWindow: any = window;
    const componentNameCamelCase = lodashString.camelCase(componentDef.componentName);


    if (anyWindow.FormbirdServiceInjector.componentFunctions
      && anyWindow.FormbirdServiceInjector.componentFunctions[componentNameCamelCase]
      && anyWindow.FormbirdServiceInjector.componentFunctions[componentNameCamelCase].process) {
      this.functionComponentData[componentDef.name] = {
        componentDef: componentDef,
        componentName: componentNameCamelCase
      };

      await anyWindow.FormbirdServiceInjector.componentFunctions[componentNameCamelCase].process(
        componentDef, this.documentData.document);
    }
  }

  registerAndAppendComponents() {
    const _self = this;
    return new Promise(function (resolve, reject) {

      let key = 0;
      let arrWrapActionOpen = [];
      let arrWrapActionClose = [];
      let notFoundPanelNames = [];

      _self.viewContainerRef.clear();

      if (!_self.components){
        _self.components = _self.documentData.template.components;
      }

      async.eachSeries(_self.documentData.template.components, async (componentDef, next) => {
        
        if (componentDef.skipRendering) {
          key++;
          next();
          return;
        }

        const componentName = componentDef.componentName;
        if (componentName) {
          const componentDoc: ComponentDocument = await _self.componentService.loadComponentDocAndVendorLibs(componentName);

          componentDef.key = key;

          await _self.runComponentFunction(componentDef);

          if (componentDoc?.componentType !== 'Function') {
            const isOpenPanel = componentDef.wrapAction === "open" && componentDef.wrapHtmlType === "wrap";
            const isClosePanel = componentDef.wrapAction === "close" && componentDef.wrapHtmlType === "wrap";
            if ((isOpenPanel || isClosePanel) && !componentDef.name) {
              notFoundPanelNames.push(key);
            }

            if (isOpenPanel) {
              arrWrapActionOpen.push(key);
            }
            if (arrWrapActionOpen.length > 0 && isClosePanel) {
              arrWrapActionClose.push(key);
            }
            if (arrWrapActionOpen.length > 0) {
              if (arrWrapActionClose.length > 0 && arrWrapActionOpen.length === arrWrapActionClose.length) {
                const item = { key: arrWrapActionOpen[0], endKey: key - 1 };

                arrWrapActionOpen = [];
                arrWrapActionClose = [];
                _self.viewContainerRef.createEmbeddedView(_self.ngTpl, { $implicit: item });
              }
            } else {
              _self.viewContainerRef.createEmbeddedView(_self.ngTpl, { $implicit: componentDef });
            }

          }

          key++;
          next();

        } else {
          key++;
          next();
        }
      }, function(errorMsg) {
        if (errorMsg) {
          logger.error(errorMsg);
          reject(new Error(errorMsg));
        } else {

          if (arrWrapActionOpen.length > 0) {

            let notFoundClosingPanelNames = [];

            for (let i = 0; i < arrWrapActionOpen.length; i++) {

              const key = arrWrapActionOpen[i];
              const panelName =  _self.components[key].name;

              const foundKey = lodashArray.findIndex(_self.components, {
                'name': panelName, 'wrapAction': 'close',
                'wrapHtmlType': 'wrap'});
              if (foundKey < 0){
                notFoundClosingPanelNames.push(panelName);
              }
            }

            _self.notificationService.error(`Closing Tag for panel:  ${notFoundClosingPanelNames.join(', ')} missing or incomplete`);

            const item = { key: arrWrapActionOpen[0], endKey: key - 1 };
            arrWrapActionOpen = [];
            arrWrapActionClose = [];
            _self.viewContainerRef.createEmbeddedView(_self.ngTpl, { $implicit: item });
          }
        }

        if (notFoundPanelNames.length) {
          _self.notificationService.error(`Panel(s) at position ${notFoundPanelNames.join(', ')} have no name field`);
        }

        resolve(true);
      });
    });
  }

  appendComponents() {
    this.components = this.documentData.template.components;
    this.documentData.template.components.forEach((componentDef: any, index) => {
      componentDef.key = index;
      this.viewContainerRef.createEmbeddedView(this.ngTpl, { $implicit: componentDef });

      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * @description
   * adding disable save field values which has been removed while saving
   */
  addDisableSaveFieldValues() {
    const document = this.documentData.document;
    const template = this.documentData.template;
    if (document && template) {
      this.documentService.setDisableSaveFieldDocument(document, template);
    }
  }

  /**
 * Determines whether a component definition represents a panel (wrap action open/close).
 * @param componentDef - The component definition to check
 * @returns True if the component is a panel
 */
  isPanel(componentDef: any) {
    const wrapAction = componentDef?.wrapAction?.toLowerCase();
    return wrapAction === 'open' || wrapAction === 'close';
  }

  watchComponentStateChange() {

    const compTemplateSub = this.recordedComponentTemplateChanges$.subscribe(changes => {

      const templateId = this.documentData.templateId;
      if (changes && templateId) {
        const fieldNames = changes[templateId] ? Object.keys(changes[templateId]) : null;
        if (fieldNames && fieldNames.length) {
          fieldNames.forEach(fieldName => {
            const tplItemChanges = changes[templateId][fieldName];
            const key = tplItemChanges.key;
            const componentDef = changes[templateId][fieldName].componentDef;
            const isPanel = this.isPanel(componentDef);
            const divId = "divParent" + fieldName + key;

            const panelWrapperId = fieldName + this.documentData.documentId;
            let children = this.el.nativeElement?.parentNode?.querySelectorAll(`[id='${divId}'`);
            if (!children?.length) {
              children = this.el.nativeElement?.parentNode?.querySelectorAll(`[id='${panelWrapperId}'`);
            }
            children?.forEach(child => {
              child.style = `display: ${componentDef.visible !== false ? 'block' : 'none'}`;

              const isMandatory = componentDef.mandatory === true;
              if (isMandatory) {
                isPanel ? child.classList.add('mandatory-panel') : child.classList.add('mandatory');
              } else {
                isPanel ? child.classList.remove('mandatory-panel') : child.classList.remove('mandatory');
              }

              const isEnabled = componentDef.enabled !== false;
              if (!isEnabled) {
                isPanel ? child.classList.add('disabled-panel', 'readonly-panel', 'fb-label-disabled') : child.classList.add('disabled', 'readonly', 'fb-label-disabled');
                isPanel ? child.classList.remove('fb-label-enabled') : child.classList.remove('fb-label-enabled');
              } else {
                isPanel ? child.classList.remove('disabled-panel', 'readonly-panel', 'fb-label-disabled') : child.classList.remove('disabled', 'readonly', 'fb-label-disabled');
                isPanel ? child.classList.add('fb-label-enabled') : child.classList.remove('fb-label-enabled');
              }
            });
          });
        }
      }

    });

    this.subs.add(compTemplateSub);

  }

  private restoreWIPDocument = (result, unsavedDocumentListId) => {
    const _self = this;
    const initialData = result ? result.restoreWIPDocument.initialData : null;
    const hasInitialized = result ? result.restoreWIPDocument.hasInitialized : false;
    if (initialData) {

      for (const key in initialData) {
        const value = initialData[key];

        if (UtilType.hasValue(value)) {
          _self.documentData.document[key] = value; 
        
          const compDef = this.documentData?.template?.components?.find(comp => comp.name === key);
          if (compDef && !compDef.disableSave && !compDef.disableDirty) {
            const docId = _self.documentData.document.documentId;
            if (!hasInitialized) {
              _self.modifiedFieldService.addModifiedField(docId, key, unsavedDocumentListId, false) ;
            }
            
          }
        }

      }

      this.appStore.dispatch(formSetDocument(this.documentData.document));
    }
  }

  private clearCache = () => {
    const _self = this;
    return new Promise(async function (resolve) {
      _self.modalService.showDialog('Clearing Cache. Please wait.', 'WARNING');
      const ok = await _self.dataService.clearData();
      _self.modalService.closeDialog();
      if (ok) {
        _self.notificationService.printMessage('Your cache is now cleared.', 'success');
        _self.offlineStatusService.offlineStatus.errorCacheAttachedFile = [];
        _self.loggedInUserService.setUserConfigItem('ErrorCachingAttachedFiles', _self.offlineStatusService.offlineStatus.errorCacheAttachedFile);
      } else {
        _self.notificationService.printMessage('Error on deleting cache.', 'error');
      }
      resolve(true);
    });

  }

  /**
   * Returns the appropriate CSS class for the loading animation state
   * @returns The CSS class name for the current loading state
   */
  getLoadingClass(): string {
    return `loading-${this.state}`;
  }
}
